import type { Pinia } from 'pinia';
import type { CustomHeaders, ServerHeaders } from '../types';

import { ApolloLink, HttpLink } from '@apollo/client/core';
import { RetryLink } from '@apollo/client/link/retry';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { map } from 'rxjs';

import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { decodeRoutePath } from '@/helpers/route-path-helper';

export function getAppVersion() {
	return `${BUILD_CONFIG.appVersion}-web-${import.meta.server ? 'ssr' : 'web'}`;
}

export function withNormalizedFullPath(): ApolloLink {
	return new ApolloLink((operation, forward) => {
		const { fullPath } = operation.variables ?? {};

		if (typeof fullPath === 'string') {
			operation.variables = {
				...operation.variables,
				fullPath: decodeRoutePath(fullPath),
			};
		}

		return forward(operation);
	});
}

export function withAccessToken(pinia: Pinia) {
	return new ApolloLink((operation, forward) => {
		const userStore = useUserStore(pinia);

		if (userStore.accessToken) {
			operation.setContext(({ headers }: { headers: any }) => ({
				headers: {
					...headers,
					Authorization: `Bearer ${userStore.accessToken}`,
				},
			}));
		}

		return forward(operation);
	});
}

/**
 * This function will be called each time we make a GraphQL query
 * or mutation.
 *
 * New users don't have an access token until they sign up.
 *
 * Since we cannot reinitialize ApolloClient once we get the access token
 * and save it as a header, this code will be checking for it on each
 * query or mutation.
 */
export function withAuthMiddleware(pinia: Pinia) {
	return new ApolloLink((operation, forward) => {
		const userStore = useUserStore(pinia);

		const customHeaders: CustomHeaders = {
			'App-Version': getAppVersion(),
		};

		if (userStore.jwId) {
			customHeaders['DEVICE-ID'] = userStore.jwId;
		}

		if (BUILD_CONFIG.globals.IS_DEV) {
			customHeaders['X-JW-Local-Dev'] = 'true';
		}

		operation.setContext(({ headers }: { headers: any }) => ({
			headers: {
				...headers,
				...customHeaders,
			},
		}));

		return forward(operation);
	});
}

export function withHeaders(customHeaders?: ServerHeaders) {
	return new ApolloLink((operation, forward) => {
		operation.setContext(({ headers = {} }) => ({
			http: {
				includeExtensions: false,
				includeQuery: true,
			},
			headers: {
				...headers,
				...customHeaders,
			},
		}));

		return forward(operation);
	});
}

/** HTTP connection to the API */
export function withHttpLink(): ApolloLink {
	return new HttpLink({
		uri: import.meta.server ? JW_CONFIG.GRAPHQL_INTERNAL_URL : JW_CONFIG.GRAPHQL_URL,
		fetch,
	});
}

/** HTTP connection to the API with file upload support. */
export function withUploadHttpLink(): ApolloLink {
	return new UploadHttpLink({
		uri: import.meta.server ? JW_CONFIG.GRAPHQL_INTERNAL_URL : JW_CONFIG.GRAPHQL_URL,
		fetch,
	});
}

export function withSGHeader(pinia: Pinia) {
	const languageStore = useLanguageStore(pinia);
	const userStore = useUserStore(pinia);
	const routingStore = useRoutingStore(pinia);
	const trackingStore = useTrackingStore(pinia);

	return new ApolloLink((operation, forward) => {
		const headerParams = {
			c: languageStore.country ?? '',
			l: languageStore.language ?? '',
			pv: trackingStore.pageViewUUID ?? '',
			d: userStore.jwId ?? '',
			p: getAppVersion() ?? '',
			pa: routingStore.activeRoute?.fullPath ?? '',
			e: (routingStore.activeRoute?.query?.new_preferred_variant as string | undefined) ?? '',
		};

		const header = new URLSearchParams(headerParams).toString();

		if (header) {
			operation.setContext(({ headers = {} }) => ({
				headers: {
					...headers,
					sg: header,
				},
			}));
		}

		return forward(operation);
	});
}

export function withRetry(): RetryLink {
	return new RetryLink({
		delay: {
			initial: 300, // ms
			max: 5000, // ms
			jitter: true, // randomize between initial and max
		},
		attempts: {
			max: 2,
			retryIf: (error, _operation) => {
				const { name, statusCode } = error;
				console.error('[RetryLink]', name, statusCode);
				return !!error;
			},
		},
	});
}

export function withResponseLogger(headers?: ServerHeaders): ApolloLink {
	console.debug(`[DEBUG_APOLLO] Apollo Request/Response Logger Enabled`);
	try {
		console.debug(`[DEBUG_APOLLO] Apollo Headers`, JSON.stringify(headers));
	} catch {}

	return new ApolloLink((operation, forward) => {
		try {
			console.debug(
				'[DEBUG_APOLLO] Request',
				JSON.stringify({ isServer: import.meta.server, context: operation.getContext() })
			);
			console.debug('\n');
		} catch {}

		return forward(operation).pipe(
			map(result => {
				try {
					console.debug(
						'[DEBUG_APOLLO] Response',
						JSON.stringify({ isServer: import.meta.server, context: operation.getContext() })
					);
					console.debug('\n');
				} catch {}

				return result;
			})
		);
	});
}
